"use client";

import { Skeleton } from "antd";
import { useIsDark } from "@/context/ThemeContext";

export default function HomeSkeleton() {
  const isdark = useIsDark();

  const skeletonBaseStyles = {
    backgroundColor: isdark ? "#374151" : undefined,
  };

  const skeletonGradientVars = {
    "--ant-skeleton-color": isdark ? "#374151" : "",
    "--ant-skeleton-to-color": isdark ? "#4B5563" : "",
  };

  return (
    <div className="mt-20 xl:mt-[50px] mb-12 xl:mb-16">
      {/* Hero section */}
      <section className="relative px-5 md:px-10 3xl:px-[150px]">
        <div className="flex w-full max-w-[2000px] flex-col mobile2:flex-row items-center justify-between laptop:min-h-screen mx-auto">
          {/* Left text block */}
          <div className="flex flex-col w-full items-center justify-center mobile2:w-1/2 mobile2:items-start space-y-4">
            <Skeleton.Input
              active
              size="large"
              className="!h-10 lg:!h-14 xl:!h-16 4xl:!h-20 !w-3/4 small:!w-full mobile2:!w-3/4"
              style={skeletonBaseStyles}
            />
            <Skeleton.Input
              active
              size="large"
              className="!h-10 lg:!h-14 xl:!h-16 4xl:!h-20 !w-3/4 small:!w-full mobile2:!w-2/3"
              style={skeletonBaseStyles}
            />
            <Skeleton.Input
              active
              size="large"
              className="!opacity-100 mobile:!opacity-0 mobile2:!opacity-100 !h-10 lg:!h-14 xl:!h-16 4xl:!h-20 !w-3/4 small:!w-full mobile2:!w-3/4"
              style={skeletonBaseStyles}
            />
            <Skeleton.Input
              active
              size="large"
              className="!opacity-100 small:!opacity-0 mobile2:!opacity-100 !h-10 lg:!h-14 xl:!h-16 4xl:!h-20 !w-3/4 mobile2:!w-2/3"
              style={skeletonBaseStyles}
            />

            {/* Subtitle lines */}
            <div className="flex flex-col w-full space-y-2 mt-2">
              <Skeleton.Input
                active
                size="small"
                className="!h-4 !w-full"
                style={skeletonBaseStyles}
              />
              <Skeleton.Input
                active
                size="small"
                className="!h-4 !w-full"
                style={skeletonBaseStyles}
              />
            </div>

            {/* Button */}
            <Skeleton.Button
              active
              size="large"
              className="!h-7 md:!h-10 lg:!h-12 xl:!h-16 4xl:!h-18 !w-48 tablet:!w-[232px] 4xl:!w-[330px] mt-2"
              style={skeletonBaseStyles}
            />
          </div>

          {/* Right cube / visual */}
          <div className="hidden mobile2:flex w-1/2 justify-end items-center">
            <Skeleton.Input
              active
              size="large"
              className="w-52! h-52! sm:w-[238px]! sm:h-[238px]! md:w-[292px]! md:h-[292px]! tablet:w-[360px]! tablet:h-[360px]! lg:w-[410px]! lg:h-[410px]! xl:w-[450px]! xl:h-[450px]! 3xl:w-[500px]! 3xl:h-[500px]! rounded-lg"
              style={skeletonBaseStyles}
            />
          </div>
        </div>
      </section>

      {/* Cards section */}
      <section className="mt-20 mx-5 md:mx-10 xl:mx-25">
        <div className="flex max-w-[2000px] mx-auto">
          <div className="hidden mobile2:block w-1/2" />

          <div className="w-full mobile2:w-1/2 grid grid-cols-2 gap-2 mobile:gap-5 mobile2:gap-2 sm:gap-3 tablet:gap-8 lg:gap-12 xl:gap-10 laptop:gap-[57px] 3xl:gap-16 4xl:gap-x-20">
            {Array.from({ length: 4 }).map((_, index) => (
              <Skeleton.Button
                key={index}
                active
                className="!h-52 sm:!h-44 md:!h-52 xl:!h-[360px] !w-full rounded-xl"
                style={skeletonBaseStyles}
              />
            ))}
          </div>
        </div>
      </section>

      {/* Divider */}
      <div className="h-28 w-full mt-[50px]" style={skeletonGradientVars}>
        <Skeleton.Input
          active
          size="large"
          className="!w-full !h-full"
          style={skeletonBaseStyles}
        />
      </div>

      {/* Services section */}
      <section className="flex flex-col max-w-[2000px] items-center mt-8 mb-8 md:mt-14 md:mb-14 xl:mt-[65px] xl:mb-0 mx-5 md:mx-10 xl:mx-25 4xl:mx-auto">
        {/* Header */}
        <Skeleton.Input
          active
          size="large"
          className="!h-12 !w-1/2 md:!w-1/4"
          style={skeletonBaseStyles}
        />
        {/* Sub header */}
        <Skeleton.Input
          active
          size="small"
          className="!h-4 !w-full mt-4 xl:mt-10"
          style={skeletonBaseStyles}
        />
        <Skeleton.Input
          active
          size="small"
          className="!h-4 !w-1/3 mt-2"
          style={skeletonBaseStyles}
        />

        <div
          className="w-full max-w-[2000px] mx-auto grid grid-cols-2 sm:grid-cols-4 gap-2 mobile:gap-x-4 sm:gap-2 tablet:gap-4 xl:gap-[37px] mt-8 md:mt-14"
          style={skeletonGradientVars}
        >
          {Array.from({ length: 8 }).map((_, index) => (
            <Skeleton.Button
              key={index}
              active
              className="aspect-[280/200] !w-full rounded-xl"
              style={skeletonBaseStyles}
            />
          ))}
        </div>
      </section>

      {/* Approach Section */}
      <section className="flex flex-col sm:flex-row max-w-[2000px] gap-4 xl:gap-12 mx-5 md:mx-10 xl:mx-[100px] mt-[50px] 4xl:mx-auto">
        {/* Left side */}
        <div className="flex flex-col items-center sm:items-start w-full sm:w-1/3 space-y-6">
          <Skeleton.Input
            active
            size="large"
            className="!h-12 !w-full sm:!w-2/5"
            style={skeletonBaseStyles}
          />
          <Skeleton.Input
            active
            size="large"
            className="!h-12 !w-3/4"
            style={skeletonBaseStyles}
          />
          <Skeleton.Input
            active
            size="large"
            className="hidden sm:!block !h-12 !w-3/5"
            style={skeletonBaseStyles}
          />
          <Skeleton.Input
            active
            size="large"
            className="hidden sm:!block !h-12 !w-3/4"
            style={skeletonBaseStyles}
          />
          <Skeleton.Input
            active
            size="large"
            className="hidden sm:!block !h-12 !w-3/5"
            style={skeletonBaseStyles}
          />

          <Skeleton.Button
            active
            size="large"
            className="!h-10 !w-32 rounded-full mt-6"
            style={skeletonBaseStyles}
          />
        </div>

        {/* Right side */}
        <div
          className="w-full mobile2:w-2/3 space-y-10"
          style={skeletonGradientVars}
        >
          {Array.from({ length: 7 }).map((_, index) => (
            <div key={index} className="flex gap-4 items-start">
              {/* circle */}
              <Skeleton.Avatar active size={40} style={skeletonBaseStyles} />

              {/* Text */}
              <div className="space-y-3 flex-1">
                <Skeleton.Input
                  active
                  size="small"
                  className="!h-5 !w-full"
                  style={skeletonBaseStyles}
                />
                <Skeleton.Input
                  active
                  size="small"
                  className="!h-4 !w-full"
                  style={skeletonBaseStyles}
                />
                <Skeleton.Input
                  active
                  size="small"
                  className="!h-4 !w-5/6"
                  style={skeletonBaseStyles}
                />
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Solutions cards */}
      <section className="flex flex-col items-center max-w-[2000px] mt-8 mb-8 md:mt-14 md:mb-14 xl:mt-[65px] xl:mb-0 mx-5 md:mx-10 xl:mx-25 4xl:mx-auto">
        {/* Header */}
        <Skeleton.Input
          active
          size="large"
          className="!h-12 !w-1/2 md:!w-1/4"
          style={skeletonBaseStyles}
        />
        {/* Sub header */}
        <Skeleton.Input
          active
          size="small"
          className="!h-4 !w-full mt-4 xl:mt-10"
          style={skeletonBaseStyles}
        />
        <Skeleton.Input
          active
          size="small"
          className="!h-4 !w-1/3 mt-2"
          style={skeletonBaseStyles}
        />

        <div
          className="w-full grid grid-cols-3 gap-2 mobile2:gap-3 tablet:gap-5 lg:gap-18 xl:gap-[90px] laptop:gap-[154px] 2xl:gap-[200px] 3xl:gap-[270px] mt-10"
          style={skeletonGradientVars}
        >
          {Array.from({ length: 3 }).map((_, index) => (
            <Skeleton.Button
              key={index}
              active
              className="!h-52 sm:!h-44 md:!h-52 xl:!h-[360px] !w-full rounded-xl"
              style={skeletonBaseStyles}
            />
          ))}
        </div>
      </section>

      {/* Feedback section */}
      <section className="flex flex-col items-center max-w-[2000px] mt-8 mb-8 md:mt-14 md:mb-14 xl:mt-[65px] xl:mb-[65px] mx-5 md:mx-10 xl:mx-25 4xl:mx-auto">
        {/* Header */}
        <Skeleton.Input
          active
          size="large"
          className="!h-12 !w-1/2 md:!w-1/4"
          style={skeletonBaseStyles}
        />
        {/* Sub header */}
        <Skeleton.Input
          active
          size="small"
          className="!h-4 !w-full mt-4 xl:mt-10"
          style={skeletonBaseStyles}
        />
        <Skeleton.Input
          active
          size="small"
          className="!h-4 !w-1/3 mt-2"
          style={skeletonBaseStyles}
        />

        {/* Feedback cards */}
        <div className="flex w-full justify-center h-[280px] sm:h-80 lg:h-[393px] mt-4 xl:mt-10">
          <Skeleton.Button
            active
            className="!h-full !w-10/12 tablet:!w-1/2 rounded"
            style={skeletonBaseStyles}
          />
          <Skeleton.Button
            active
            className="hidden tablet:!block !h-full !w-1/2 rounded ml-5"
            style={skeletonBaseStyles}
          />
        </div>
      </section>
    </div>
  );
}
